import React from 'react';
import { Link } from 'react-router-dom';

import { Label } from 'office-ui-fabric-react';
import { TextField } from 'office-ui-fabric-react/lib/TextField';
import { ComboBox } from 'office-ui-fabric-react/lib/ComboBox';
import { Toggle } from 'office-ui-fabric-react/lib/Toggle';
import { Checkbox } from 'office-ui-fabric-react/lib/Checkbox';

import { mustBeNumber } from '../validation/validators'
import DomainListService from '../services/DomainListService';
import SurveyService from '../services/SurveyService'
import Survey from '../models/SurveyModel';

import FilteredMultiSelection from '../common/FilteredMultiSelection'
import { sortColumnsByDisplayOrder, getSelectedTemplates, getDataFromState } from '../common/Utilities';

import '../App.css';

class ConfigureSurveyTemplate extends React.Component {

    constructor(props) {
        super(props);

        const sharedState = { ...props.data };
        sharedState.selectedTemplates = getSelectedTemplates(props.data.survey);
        // Get the key of the first item in the array
        sharedState.selectedTemplateKey = sharedState.selectedTemplateKey === "" ? sharedState.selectedTemplates[0] : sharedState.selectedTemplateKey;
        const localState = {

            hasChanges: false,
            
            // Array to hold all of the items that will be displayed in the list
            _availableFields: [],
            
            columnOptions: [],

            // Select column that shows in the editor panel
            selectedRecord: null
        };
        this.state = { ...sharedState, ...localState };
    }

    async componentDidMount() {

        const db = new DomainListService();
        // Collection to hold the available datatypes
        if (!(this.state.datatypes && this.state.datatypes.length>0)) {
            db.getDomainList(db.LIST_TYPE.DATA_TYPES, (data) => {
                this.setState({ datatypes: data });
            });
        }

        // Add any templates to the survey that don't exist
        const survey = this.state.survey;
        const templateCount = this.state.survey.templateCount();
        this.state.selectedTemplates.forEach(item => {
            survey.addTemplateIfMissing(item, this.templateLabel(item));
        });
        // Remove any Surveyes that are not in the selectedTemplates collection
        this.state.survey.removeTemplatesNotInList(this.state.selectedTemplates);

        // if the count changed then a new template was added
        if (this.state.survey.templateCount() !== templateCount) {
            this.setState({ survey: survey});
        }
        // Array to hold all of the items that will be displayed in the list
        this.setState({
            _availableFields: survey.getColumns(this.state.selectedTemplateKey).sort(sortColumnsByDisplayOrder)
        });
    };

    templateLabel = (value) => {
        const item = this.state.templates.find((item) => item.value === value);
        return item ? item.label : `Error - Template ${value} not found`;
    };

    templateLabels = () => {
        return this.state.selectedTemplates.map(item => {
            return { key:item, text: this.templateLabel(item) };
        });
    };

    onTemplateChange = (e, option, index, value) => {
        if (option !== undefined) {
            this.setState({
                selectedTemplateKey: option.key,
                _availableFields: this.state.survey.getColumns(option.key).sort(sortColumnsByDisplayOrder),
                selectedRecord: null
            });
        }
    };

    saveRecordOnly = () => {
        this.saveChanges(0);
    };
    saveAndNext = () => {
        this.saveChanges(1);
    };
    saveAndPrevious = () => {
        this.saveChanges(-1);
    };
    saveChanges = (moveTo) => {
        // Set the timestamp before saving. It's a new record - 
        // this tells when it was updated and not to validate if a user does a back and changes something
        const timeStamp = (new Date()).toISOString();
        let survey = {...this.state.survey}
        survey["schemaDTS"] = timeStamp;
        this.setState({survey: survey}, () => {
            // Save the survey
            const db = new SurveyService();
            db.put(new Survey().fromSimple(survey).getDbObject(), this.state.templates);
            this.setState({hasChanges: false });
            this.props.onSave(getDataFromState(this.state), moveTo);
            this.props.onSurveyChange(false);
        });
    };

    // This puts the modified value into the selectedRecord,
    // which is the same as the actual record in the survey.Templates
    // because the selectedRecord is a reference to the actual record
    setTemplateValue = (columnName, value) => {
        let item = this.state.selectedRecord;        
        switch (columnName) {
            case "Label":
            case "Hint":
                item[columnName].S = value;
                break;
            case "Length":
                item[columnName].N = value;
                break;
            case "DataType":
                item[columnName].S = value.key;
                break;
            case "SelectList":
                let listItems = value.split("\n");
                if (listItems.length > 0 && listItems[0] !== "") {
                    item[columnName].L = listItems.map(t => { return { S: t } });
                }
                else {
                    item[columnName].L = [];
                }
                break;
            case "Nullable":
                item[columnName].BOOL = value;
                break;
            default:
                throw new Error("Unsupported column type");
        }
        
        if (Object.keys(item[columnName]).length > 1 && item[columnName].hasOwnProperty('NULL')) {
            delete item[columnName].NULL;
        }
        this.setState({ hasChanges: true, selectedRecord: item });
        this.props.onSurveyChange(true);
    }


    // Makes sure the columns exist in the supplied record that have to
    // exist for this form
    getVerifiedRecord = (columnRecord) => {
        const requiredFields = [
            { name: "ColumnName", type: "S"},
            { name: "Label", type: "S"},
            { name: "DataType", type: "S"},
            { name: "Length", type: "N"},
            { name: "SelectList", type: "S"},
            { name: "Hint", type: "S"},
            { name: "Nullable", type: "BOOL", default: true},
        ];
        requiredFields.forEach(c => {
            if (!columnRecord[c.name]) {
                columnRecord[c.name] = { };
                columnRecord[c.name][c.type] = (c.default ? c.default : "");
            }
        });
        // Validate the data type - by converting case if necessary
        const dt = columnRecord["DataType"].S;
        const dtLower =  dt.toLowerCase();
        let types = this.state.datatypes;
        for (let i=0; i<types.length; i++) {
            if (types[i].key.toLowerCase() === dtLower && types[i].key !== dt) {
                columnRecord["DataType"].S = types[i].key;
                break;
            }
        };
        return columnRecord;
    };

    onColumnSelected = (e) => {
        this.setState({
            selectedRecord: this.getVerifiedRecord(this.state._availableFields.find(c => c.ColumnName.S === e[0].key))
        });
    };
    
    getFieldSValue = (fieldName) => {
        return this.state.selectedRecord[fieldName].S;
    };
    getFieldNValue = (fieldName) => {
        return this.state.selectedRecord[fieldName].N;
    };
    getFieldBValue = (fieldName) => {
        return (typeof(this.state.selectedRecord[fieldName]) === "undefined") ? false : this.state.selectedRecord[fieldName].BOOL;
    };

    getSListValues = fieldName => {
        return (this.state.selectedRecord[fieldName] && this.state.selectedRecord[fieldName].L)
            ? this.state.selectedRecord[fieldName].L.map(item => item.S).join("\n")
            : [];
    }

    checkboxStyles = () => {
        return {
          root: {
            width: '200px'
          }
        };
      };

   

      //var makeOption=function(val) { return <option key={val} value={val}>{val}</option>};    
      //keysOptions=keys.map(function(key) { return makeOption(key); });

      editableProperties = () => {
        return (
            <div>
                <Label>Field Properties</Label>
                <table className="field-properties">
                    <tbody>
                    <tr>
                        <td style={{ width:"20%"}}>Field Name:</td>
                        <td><TextField label="" disabled={true} value={this.getFieldSValue("ColumnName")} /></td>
                    </tr>
                    <tr>
                        <td>Label:</td>
                        <td><TextField label="" value={this.getFieldSValue("Label")}
                            onChange={(e, newValue) => this.setTemplateValue("Label", newValue)}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Data Type:</td>
                        <td>
                            <TextField label="" value={this.getFieldSValue("DataType")} disabled={true} />
                            {/* <ComboBox
                            selectedKey={this.getFieldSValue("DataType")}
                            allowFreeform={false}
                            autoComplete="off"
                            readOnly={true}
                            onChanged={(e) => this.setTemplateValue("DataType", e)}
                            options={this.state.datatypes} /> */}
                        </td>
                    </tr>
                    <tr>
                        <td>Length:</td>
                        <td><TextField label="" value={this.getFieldNValue("Length")}
                            disabled={true} onGetErrorMessage={mustBeNumber}
                                //onChanged={(e) => this.setTemplateValue("Length", e)}
                            />
                        </td>
                    </tr>
                    {/* <tr>
                        <td>Default Value:</td>
                        <td><TextField label="" value={this.getFieldSValue("DefaultValue")} /></td>
                    </tr> */}
                    <tr>
                        <td>Select List:</td>
                        <td><TextField label="" multiline rows={6} value={this.getSListValues("SelectList")}
                                onChange={(e, newValue) => this.setTemplateValue("SelectList", newValue)}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td>Hint:</td>
                        <td><TextField label="" multiline rows={3} value={this.getFieldSValue("Hint")}
                                onChange={(e, newValue) => this.setTemplateValue("Hint", newValue) }
                            />
                        </td>
                    </tr>
                    <tr>
                        <td></td>
                        <td>
                            <Checkbox
                                label="Required Value"
                                checked={!this.getFieldBValue("Nullable")}
                                onChange={(e,checked) => {this.setTemplateValue("Nullable", !checked)}}
                                />
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        )
      }
      requiredProperties = () => {
        return (
            <div>
                <Label>Required Column Field Properties</Label>
                <table className="field-properties">
                <tbody>
                <tr>
                    <td style={{ width:"20%"}}>Field Name:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("ColumnName")} /></td>
                </tr>
                <tr>
                    <td>Label:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("Label")} /></td>
                </tr>
                <tr>
                    <td>Data Type:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("DataType")} /></td>
                </tr>
                <tr>
                    <td>Length:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldNValue("Length")} /></td>
                </tr>
                <tr>
                    <td>Select List:</td>
                    <td><TextField label="" multiline rows={6} disabled={true} value={this.getSListValues("SelectList")} /></td>
                </tr>
                <tr>
                    <td>Hint:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("Hint")} /></td>
                </tr>
                <tr>
                    <td></td>
                    <td>
                        <Toggle checked={this.getFieldBValue("Nullable")} disabled={true} label="Allow Null Values" onText="Yes" offText="No" />
                    </td>
                </tr>
                </tbody>
            </table>
            </div>
        )
      }
      separatorProperties = () => {
        return (
            <div>
                <Label>Field Separator</Label>
                <table className="field-properties">
                <tbody>
                <tr>
                    <td style={{ width:"20%"}}>Field Name:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("ColumnName")} /></td>
                </tr>
                <tr>
                    <td>Label:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("Label")} /></td>
                </tr>
                <tr>
                    <td>Data Type:</td>
                    <td><TextField label="" disabled={true} value={this.getFieldSValue("DataType")} /></td>
                </tr>
                <tr>
                    <td></td>
                    <td>
                        <table>
                            <tbody>
                                <tr>
                                    <td>
                                        <Toggle checked={this.getFieldBValue("RequiredColumn")} disabled={true} label="Required" onText="Yes" offText="No" />
                                    </td>
                                    <td>
                                        <Toggle checked={this.getFieldBValue("TemplateDefault")} disabled={true} label="Template Default" onText="Yes" offText="No" />
                                    </td>
                                    <td>
                                        <Toggle checked={this.getFieldBValue("Nullable")} disabled={true} label="Allow Null Values" onText="Yes" offText="No" />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </td>
                </tr>
                </tbody>
            </table>
            </div>
        )
      }
      fieldProperties = () => {
        if (this.getFieldSValue("DataType") === "Separator") {
            return this.separatorProperties();
        } else if (this.getFieldBValue("RequiredColumn")) {
            return this.requiredProperties();
        } else {
            return this.editableProperties();
        }
      }

  render() {
    return (
      <div className="container-fluid">
        <div className="row">
        <div className="col-sm-10">
        <h2 className="heading" >Survey Template</h2>
        <div style={{ marginTop: "25px" }}>

        </div>
        <div style={{ marginTop: "25px" }}>
        {
            (this.state.selectedTemplates.length === 1) ?
                <Label styles={this.templateNameStyles}>{this.templateLabel(this.state.selectedTemplates[0])}</Label> :
                <ComboBox
                    selectedKey={this.state.selectedTemplateKey}
                    allowFreeform={false}
                    autoComplete="off"
                    onChange={this.onTemplateChange}
                    options={this.templateLabels()}
                />
        }
        {            
            (this.state.survey.columnCount(this.state.selectedTemplateKey) === 0 && 
                this.state.selectedTemplatesChosenFields[this.state.selectedTemplateKey].length === 0) ?
            <p className="alert alert-danger">This template has no columns defined. Please return to the previous screen to select columns</p> :
            <p></p>
        }
        </div>
        <div className="row" style={{ margin: "25px 0 0 0" }}>
            { this.state._availableFields && <div>
                <FilteredMultiSelection
                buttonText="" className="col-xs-4 gray-border"
                height="425px"
                classNames={{
                    filter: 'form-control',
                    select: 'form-control filter-select-box',
                    button: 'btn btn-block btn-default',
                    buttonActive: 'btn btn-block btn-primary'
                }}
                onChange={this.onColumnSelected}
                selectFiresOnChange={true}
                multiSelect={false}
                options={this.state._availableFields.map(c => {
                     return {
                        key: c.ColumnName.S,
                        text:c.Label.S,
                        required: (typeof(c.RequiredColumn) === "undefined") ? false : c.RequiredColumn.BOOL,
                        isSeparator: c.DataType.S === "Separator",
                        allowRequiredSelection: true
                        }
                    })}
                textProp="text"
                valueProp="key"
                requiredProp="required"
            />
                {/* <select size="10" className="col-xs-4 gray-border"
                    onChange={this.onColumnSelected} style={{ height: "460px", overflow:"auto", padding: 0 }}>
                    { this.state._availableFields.map(c => (
                        <option key={c.ColumnName.S} value={c.ColumnName.S}>{c.Label.S}</option>
                      ))
                    }
                </select> */}
                </div>
            }
            <div className="col-xs-8 gray-border" style={{ height: "460px", overflow:"auto" }}>
            { this.state.selectedRecord !== null &&
                <div>
                    { this.fieldProperties() }
                </div>
            }
            </div>
        </div>
        <div className="buttonRow" style={{ marginTop: "15px" }}>
            <div>
                            <button style={{ 'fontSize': '12px', 'color': '#676767' }} type="button" onClick={this.saveAndPrevious} className="btn btn-default">Previous</button>
            </div>
            <div>
                            <button style={{ 'fontSize': '12px', 'color': '#676767' }} type="button" onClick={this.saveAndNext} className="btn btn-default">Next</button>
            </div>
            <div>
                            <button style={{ 'fontSize': '12px', 'color': '#676767' }} type="button" onClick={this.saveRecordOnly} disabled={this.state.hasChanges ? "" : "disabled"} className="btn btn-default">Save</button>
            </div>
            <div className="col-xs-3">
                            <button style={{ 'fontSize': '12px', 'color': '#676767' }}type="button" className="btn btn-default">Undo Changes</button>
            </div>
            <div>
                <Link to="/">
                                <button style={{ 'fontSize': '12px', 'color': '#676767' }} className="btn btn-default">Home</button>
                </Link>
            </div>
        </div> 

        </div>
        </div>

      </div>
    );
  }
}

export default ConfigureSurveyTemplate;
